<!-- Without this every showOkMessage in this dialog - duplicate name, validation errors,
     the feature kind rule - resolves nowhere and Ok reads as a dead button. -->
<p-confirmDialog [closable]="false" [key]="dialogKey" [baseZIndex]="1000"></p-confirmDialog>
<div *ngIf="isPageReady" class="flex flex-col h-full">
  <div class="qb-content-card flex-1">
    <p-tabView (onChange)="onTabChanged($event)" class="qb-tabview">
      <p-tabPanel header="Information">
        <div class="qb-detail-card w-full h-fit">
          <div class="flex flex-col gap-3">
            <!-- Row 1: Name (6) + Status (6) -->
            <div class="grid grid-cols-12 gap-3">
              <qb-field label="Name" class="col-span-6 lg:col-span-6">
                <input #firstInput [(ngModel)]="manufacturer.name" [bzModel]="manufacturer" name="name" pInputText />
              </qb-field>
              <qb-field label="Status" class="col-span-6 lg:col-span-6">
                <p-dropdown
                  [(ngModel)]="manufacturer.activeStatusId"
                  [bzModel]="manufacturer"
                  name="activeStatusId"
                  [options]="activeStatuses"
                  optionValue="id"
                  optionLabel="name"
                />
              </qb-field>
            </div>
            <!-- Row 2: Description (12) -->
            <div class="grid grid-cols-12 gap-3">
              <qb-field label="Description" class="col-span-12">
                <input [(ngModel)]="manufacturer.description" [bzModel]="manufacturer" name="description" pInputText />
              </qb-field>
            </div>
            <!-- Row 3: Lead Time (4) + Transit Time (4) + Doc Submit Type (4) -->
            <div class="grid grid-cols-12 gap-3">
              <qb-field label="Lead Time (Days)" class="col-span-6 lg:col-span-4">
                <p-inputNumber [(ngModel)]="manufacturer.leadTimeDays" [bzModel]="manufacturer" name="leadTimeDays" />
              </qb-field>
              <qb-field label="Transit Time (Days)" class="col-span-6 lg:col-span-4">
                <p-inputNumber [(ngModel)]="manufacturer.transitTimeDays" [bzModel]="manufacturer" name="transitTimeDays" />
              </qb-field>
              <qb-field label="Doc Submit Type" class="col-span-6 lg:col-span-4">
                <p-dropdown
                  [(ngModel)]="manufacturer.poDocSubmitTypeId"
                  [bzModel]="manufacturer"
                  name="poDocSubmitTypeId"
                  [options]="docSubmitTypes"
                  optionValue="id"
                  optionLabel="name"
                />
              </qb-field>
            </div>
            <!-- Row 3b: Manufacturer Feed (4) -->
            <div class="grid grid-cols-12 gap-3">
              <qb-field label="Manufacturer Feed" class="col-span-6 lg:col-span-4">
                <p-dropdown
                  [ngModel]="manufacturer.manufacturerFeedStatusId"
                  (ngModelChange)="onFeedStatusChange($event)"
                  [bzModel]="manufacturer"
                  name="manufacturerFeedStatusId"
                  [options]="manufacturerFeedStatuses"
                  optionValue="id"
                  optionLabel="name"
                />
              </qb-field>
              <!-- mt-6 clears the field label above the dropdown so the two line up. -->
              <div class="col-span-6 lg:col-span-8 flex items-center mt-6 text-sm"
                   style="color: var(--p-text-muted-color, #6b7280)">
                <span *ngIf="manufacturer.manufacturerFeedStatusId === ManufacturerFeedStatusEnum.Available">
                  The manufacturer has a feed. Product types show their differences from the feed.
                </span>
              </div>
            </div>

            <!-- Called out rather than left as another field row: this is the one setting here
                 that makes the system refuse a save elsewhere. Hidden only when there is no feed
                 AND nothing is configured - see showKindChooser. -->
            <div *ngIf="showKindChooser" class="grid grid-cols-12 gap-3">
              <div class="col-span-12 qb-detail-card qb-primary-tint p-4">
                <div class="font-semibold text-base mb-3 border-b pb-2 uppercase tracking-wide">
                  Each Product Type Needs
                </div>
                <div class="flex items-start gap-3 flex-wrap">
                  <!-- Pick the same kind twice to ask for two of it: trousers published as a
                       waist feature and a length feature is two Size slots and one Color. -->
                  <!-- trackBy is REQUIRED, not a tidy-up. ngFor tracks by value by default and
                       these are primitives that are meant to repeat - four nulls, then two
                       Sizes - so the differ cannot tell the slots apart and shuffles the
                       ngModel bindings onto neighbouring dropdowns. -->
                  <p-dropdown
                    *ngFor="let slot of kindSlots; let i = index; trackBy: trackBySlotIndex"
                    [(ngModel)]="kindSlots[i]"
                    [ngModelOptions]="{ standalone: true }"
                    (onChange)="collapseKindSlots()"
                    [options]="kindOptions"
                    optionValue="id"
                    optionLabel="name"
                    [style]="{ width: '11rem' }"
                  />
                </div>
                <div class="text-sm mt-3" style="color: var(--p-text-muted-color, #6b7280)">
                  {{ requiredShapeText }}
                </div>
                <!-- Said on screen because the storage makes it unavoidable: the boxes hold a
                     count per kind, not a sequence, so 'Size, Color, Size' comes back grouped
                     and looks like the system changed the answer. -->
                <div *ngIf="hasAnyKindSelected" class="text-sm mt-1"
                     style="color: var(--p-text-muted-color, #6b7280)">
                  The order of the boxes does not make a difference. The system puts the same
                  kind together when you open this again.
                </div>
                <div *ngIf="showOrphanWarning" class="text-sm mt-1"
                     style="color: var(--p-text-muted-color, #6b7280)">
                  This manufacturer has no feed. The system still refuses a style that does not
                  match. To stop the check, set every box to '(none)'.
                </div>
              </div>
            </div>
            <!-- Row 4: Account Reference (6) + Image Handler (6) -->
            <div class="grid grid-cols-12 gap-3">
              <qb-field label="Account Reference" class="col-span-6 lg:col-span-6">
                <input [(ngModel)]="manufacturer.accountReferenceName" [bzModel]="manufacturer" name="accountReferenceName" pInputText />
              </qb-field>
              <!-- The feed publishes the imagery once a manufacturer has one, so uploading here
                   would put a picture beside the manufacturer's own that nothing keeps in step. -->
              <div class="col-span-6 lg:col-span-6 flex items-center gap-2 mt-6">
                <app-image-handler
                  *ngIf="!isFeedEnabled"
                  [oldImages]="manufacturer.manufacturerImages"
                  (createJoinEntity)="createImageJoinEntity($event)"
                ></app-image-handler>
                <span *ngIf="isFeedEnabled" class="text-sm" style="color: var(--p-text-muted-color, #6b7280)">
                  The feed supplies the images for this manufacturer.
                </span>
              </div>
            </div>
          </div>
        </div>
      </p-tabPanel>
      <p-tabPanel header="Account Service Info">
        <div class="qb-detail-card w-full h-fit">
          <div class="flex flex-col gap-3">
            <app-address-component #warehouseAddress [entity]="manufacturer" [prefix]="'wh'" [isReadonly]="false" [showValidateButton]="false"></app-address-component>
            <!-- Row: Warehouse Contact (6) + Purchase Order E-mail (6) -->
            <div class="grid grid-cols-12 gap-3">
              <qb-field label="Account Service Contact" class="col-span-6 lg:col-span-6">
                <input [(ngModel)]="manufacturer.whContact" [bzModel]="manufacturer" name="whContact" pInputText />
              </qb-field>
              <qb-field label="Account Service E-mail" class="col-span-6 lg:col-span-6">
                <input [(ngModel)]="manufacturer.whEmail" [bzModel]="manufacturer" name="whEmail" pInputText />
              </qb-field>
            </div>
          </div>
        </div>
      </p-tabPanel>
      <p-tabPanel header="Billing Info">
        <div class="qb-detail-card w-full h-fit">
          <div class="flex flex-col gap-3">
            <app-address-component
              #billingAddress
              [entity]="manufacturer"
              [prefix]="'billing'"
              [showValidateButton]="false"
              [isReadonly]="false"
            ></app-address-component>
            <!-- Row: Billing Contact (6) + Billing E-mail (6) -->
            <div class="grid grid-cols-12 gap-3">
              <qb-field label="Billing Contact" class="col-span-6 lg:col-span-6">
                <input [(ngModel)]="manufacturer.billingContact" [bzModel]="manufacturer" name="billingContact" pInputText />
              </qb-field>
              <qb-field label="Billing E-mail" class="col-span-6 lg:col-span-6">
                <input [(ngModel)]="manufacturer.billingEmail" [bzModel]="manufacturer" name="billingEmail" pInputText />
              </qb-field>
            </div>
          </div>
        </div>
      </p-tabPanel>
    </p-tabView>
  </div>

  <div class="flex flex-row gap-2 justify-start">
    <button
      [disabled]="!this.uow.hasChanges() || hasValidationErrors"
      (click)="ok()"
      pButton
      type="button"
      label="Ok"
      title="Ok"
      icon="pi pi-check"
      class="p-button-raised"
    ></button>
    <button (click)="cancel()" pButton type="button" label="Cancel" title="Cancel" icon="pi pi-times" class="p-button-raised"></button>
  </div>
</div>
